import { Injectable } from '@angular/core';
import { MapData } from '../jurisdiction/location-form/MapData';
import config from '../../assets/config/config.json';
import configProd from '../../assets/config/config.prod.json';
import configBase from '../../assets-base/config/config.json';
import configProdBase from '../../assets-base/config/config.prod.json';
import {
  ApplicationTypeFilters,
  ChimneySweeperFilters,
  Data,
  FacilitiesHeatingHiddenFields,
  FacilitiesHiddenDropdownData,
  FacilitiesPowerHiddenFields,
  FacilitiesWaterHiddenFields,
  IConfig,
  Labels,
  MandatoryDocumentsPerCommodity,
  MediaToShow,
  NavigationMenu,
  OptionalDocuments,
  OptionalDocumentsByCommodity,
  OptionalDocumentsByQualification,
  MandatoryDocumentsForGuest,
  MandatoryDocumentsForGuestByCommodity,
  MandatoryDocumentsForGuestByQualification,
  Panel,
  PrivacyPolicy,
  TechnicianDataHeatingHiddenFields,
  TechnicianDataWaterHiddenFields,
  TechnicianRegistration,
  MandatoryDocumentsByQualificationAndCommodity,
  FieldSettings,
} from '../interface/config.interface';
import { KeyValueData } from '../domains/common.domain';
import { BaseMandatoryDocumentsPerCommodity } from '../constants/app.constants';
import { AreaOption } from '../model/area-option.model';
import _ from 'lodash';
import '../common/mixins/lodash.mixins';

@Injectable() // TODO: why is this not called ConfigService?
export class EnvironmentService {
  private configData: IConfig = null; // TODO: typesafe interface for configData

  constructor() {
    if (this.configData) return;

    const clientConfigCopy = JSON.parse(JSON.stringify(config)); // assets/config.json
    const baseConfigCopy = JSON.parse(JSON.stringify(configBase)); // assets-base/config.json

    this.configData = baseConfigCopy;
    this.configData = this.mergeConfig(clientConfigCopy, baseConfigCopy);

    const isProd = (window as any).gop_stage === 'prod';

    if (isProd) {
      const baseProdConfigCopy = JSON.parse(JSON.stringify(configProdBase)); // assets-base/config.prod.json
      const clientProdConfigCopy = JSON.parse(JSON.stringify(configProd)); // assets/config.prod.json
      const prodConfig = this.mergeConfig(clientProdConfigCopy, baseProdConfigCopy);
      this.configData = this.mergeConfig(prodConfig, this.configData);
    }
  }

  private mergeConfig(fromConfig: any, toConfig: any): any {
    if (fromConfig.navigationMenu) {
      toConfig.navigationMenu = _.mergeByKey(fromConfig.navigationMenu || [], toConfig.navigationMenu || [], 'id');
    }

    return _.merge(toConfig, fromConfig);
  }

  get shouldSerializeCustomFields(): boolean {
    return true;
  }

  get apiUrl(): string {
    return '/api/';

    // stw: workaound for azure build - needs to be activated before creating a test release
    //      and needs to be deactivated directly afterwards again
    //return 'https://mobil.bittner-krull.de/Argos.ServicePortal/Entw/SSW/Inubit/';
    // stw: workaround for customer cloud build might be slightly different
    //      check url before building the prod release
    // return this.configData?.urls?.[environment.stage].apiUrl;
  }

  get meterTypeControlUnit(): boolean {
    return this.configData?.appPages?.technicianData.meterTypeControlUnit;
  }

  get hideBaseDataInfoForWater(): boolean {
    return this.configData?.appPages?.facilitiesData.hideBaseDataInfoForWater;
  }

  get showPluginMountingWarning() {
    return this.configData?.appPages?.technicianData.showPluginMountingWarning;
  }

  get hideMeterDataAmount(): boolean {
    return this.configData?.appPages?.technicianData.hideMeterDataAmount || false;
  }

  get showInfoForCommodities(): boolean {
    return this.configData?.appPages?.technicianData.showInfoForCommodities;
  }

  get showRegistrationInputField(): boolean {
    return this.configData?.appPages?.technicianData.showRegistrationInputField;
  }

  get showTransformerCustomerLabel(): boolean {
    return this.configData?.appPages?.technicianData.showTransformerCustomerLabel;
  }

  get showMeterCharacteristicDetails(): boolean {
    return this.configData?.appPages?.technicianData.showMeterCharacteristicDetails;
  }

  get showWordFormat(): boolean {
    return this.configData?.appPages?.wordFormat.showWordFormat;
  }

  get showAllCustomerRadioOption(): boolean {
    return this.configData?.appPages?.notifications?.showAllCustomerRadioOption ?? false;
  }

  get shouldInternalRolesBeAbleToEditApplication(): boolean {
    return this.configData?.appPages?.lockUnlockSection.shouldInternalRolesBeAbleToEditApplication;
  }

  get allLabels(): Labels {
    return this.configData?.labels;
  }

  get customer(): string {
    return this.configData?.customer;
  }

  get allPanels(): Panel[] {
    return this.configData?.appPages?.panel;
  }

  get technicianRegistration(): TechnicianRegistration {
    return this.configData?.technicianRegistration;
  }

  get dataForConcessionValidationScenario(): 'Manual' | 'Mixed' {
    return this.configData?.technicianRegistration?.dataForConcessionValidationScenario;
  }

  get showConcessionDistrictHeating(): boolean {
    return this.configData?.technicianRegistration?.showConcessionDistrictHeating;
  }

  get showAuxiliaryRadioButton(): boolean {
    return this.configData?.technicianRegistration.showAuxiliaryRadioButton;
  }

  get hideEntryInTradeRegisterDocument(): boolean {
    return this.configData?.technicianRegistration.hideEntryInTradeRegisterDocument || false;
  }

  get showBaseDataEmail(): boolean {
    return this.configData?.technicianRegistration?.showBaseDataEmail || null;
  }

  get showTechnicianDataEmail(): boolean {
    return this.configData?.technicianRegistration?.showTechnicianDataEmail || null;
  }

  get showGuildMember(): boolean {
    return this.configData?.technicianRegistration?.showGuildMember || false;
  }

  get showMastersExaminationOn(): boolean {
    return this.configData?.technicianRegistration?.showMastersExaminationOn || null;
  }

  get showMainBusinessesPartTimeRadioBtn(): boolean {
    return this.configData?.technicianRegistration?.showMainBusinessesPartTimeRadioBtn || null;
  }

  get showInAuxiliaryOperationRadioBtn(): boolean {
    return this.configData?.technicianRegistration?.showInAuxiliaryOperationRadioBtn || null;
  }

  get showCompanyDescription(): boolean {
    return this.configData?.technicianRegistration?.showCompanyDescription || false;
  }

  get showEducationInformationSection(): boolean {
    return this.configData?.technicianRegistration?.showEducationInformationSection || null;
  }

  get showMandatoryDocument(): boolean {
    return this.configData?.technicianRegistration?.showMandatoryDocument || null;
  }

  get showProofOfQualification(): boolean {
    return this.configData?.technicianRegistration?.showProofOfQualification || null;
  }

  get showOneDayTrainingTrwi(): boolean {
    return this.configData?.technicianRegistration?.showOneDayTrainingTrwi || false;
  }

  get hideDocumentsBasedOnGasOrWaterInput(): boolean {
    return this.configData?.technicianRegistration?.hideDocumentsBasedOnGasOrWaterInput || false;
  }

  get mandatoryDocumentsPerCommodity(): MandatoryDocumentsPerCommodity {
    return (
      this.configData?.technicianRegistration?.mandatoryDocumentsPerCommodity || {
        power: BaseMandatoryDocumentsPerCommodity,
        gas: BaseMandatoryDocumentsPerCommodity,
        water: BaseMandatoryDocumentsPerCommodity,
        districtHeating: BaseMandatoryDocumentsPerCommodity,
      }
    );
  }

  get mandatoryDocumentsByQualificationAndCommodity(): MandatoryDocumentsByQualificationAndCommodity[] {
    return this.configData?.technicianRegistration?.mandatoryDocumentsByQualificationAndCommodity || null;
  }

  get mandatoryDocumentsForGuest(): MandatoryDocumentsForGuest {
    return this.configData?.technicianRegistration?.mandatoryDocumentsForGuest || null;
  }

  get mandatoryDocumentsForGuestByCommodity(): MandatoryDocumentsForGuestByCommodity[] {
    return this.configData?.technicianRegistration?.mandatoryDocumentsForGuest?.mandatoryDocumentsForGuestByCommodity || null;
  }

  get mandatoryDocumentsForGuestByQualification(): MandatoryDocumentsForGuestByQualification[] {
    return this.configData?.technicianRegistration?.mandatoryDocumentsForGuest?.mandatoryDocumentsForGuestByQualification || null;
  }

  get optionalDocuments(): OptionalDocuments {
    return this.configData?.technicianRegistration?.optionalDocuments || null;
  }

  get optionalDocumentsByCommodity(): OptionalDocumentsByCommodity[] {
    return this.configData?.technicianRegistration?.optionalDocuments?.optionalDocumentsByCommodity || null;
  }

  get optionalDocumentsByQualification(): OptionalDocumentsByQualification[] {
    return this.configData?.technicianRegistration?.optionalDocuments?.optionalDocumentsByQualification || null;
  }

  get applicationTypeFilters(): ApplicationTypeFilters[] {
    return this.configData?.applicationTypeFilters;
  }

  get chimneySweeperFilters(): ChimneySweeperFilters {
    return this.configData?.chimneySweeperFilters;
  }

  get navigationMenu(): NavigationMenu[] {
    return this.configData?.navigationMenu;
  }

  get notificationDelayTime(): number {
    return this.configData?.notificationDelayTime;
  }

  get privacyPolicy(): PrivacyPolicy {
    return this.configData?.privacyPolicy;
  }

  get impressumRoute(): string {
    return this.configData?.dashboard?.impressumRoute;
  }

  get showRegisteredChimneySweepersTab(): boolean {
    return this.configData?.appPages?.employeeOverview.showRegisteredChimneySweepersTab;
  }

  get showRegisteredChimneySweepersIcons(): boolean {
    return this.configData?.appPages?.employeeOverview.showRegisteredChimneySweepersIcons;
  }

  get standardTechnicianEnabled(): boolean {
    return this.configData?.appPages?.employeeOverview.standardTechnicianEnabled;
  }

  get dontNotifyTechnicianOnAppplicationReject(): boolean {
    return !!this.configData?.appPages?.employeeOverview?.dontNotifyTechnicianOnAppplicationReject;
  }

  get showRequestChangeBtn(): boolean {
    return this.configData?.appPages?.employeeOverview.showRequestChangeBtn;
  }

  get showCorrectOfferButton(): boolean {
    return this.configData?.appPages?.employeeOverview.showCorrectOfferButton;
  }

  get employeeOverviewHasWhiteTabBackground(): boolean {
    return this.configData?.appPages?.employeeOverview.employeeOverviewHasWhiteTabBackground;
  }

  get districtRequired(): boolean {
    return this.configData?.appPages?.location.districtRequired;
  }

  get mediaToShow(): MediaToShow {
    return this.configData?.appPages?.location.mediaToShow;
  }

  get showOrderMessage(): boolean {
    return this.configData?.appPages?.location.showOrderMessage;
  }

  get showConversionOptions(): boolean {
    return this.configData?.appPages?.location.showConversionOptions;
  }

  get showNewDevAreaDropdownForConnection(): boolean {
    return this.configData?.appPages?.location.showNewDevAreaDropdownForConnection;
  }

  get showNewDevAreaDropdownForInstallation(): boolean {
    return this.configData?.appPages?.location.showNewDevAreaDropdownForInstallation;
  }

  get showNewDevAreaDetails(): boolean {
    return this.configData?.appPages?.location.showNewDevAreaDetails;
  }

  get showWarningText(): boolean {
    return this.configData?.appPages?.location.showWarningText;
  }

  get showDecommissioningInFeedInSystem(): boolean {
    return this.configData?.appPages?.location.showDecommissioningInFeedInSystem;
  }

  get showGPS(): boolean {
    return this.configData?.appPages?.location.showGPS;
  }

  get showParcelNumberTextBox(): boolean {
    return this.configData?.appPages?.location?.showParcelNumberTextBox;
  }

  get isAddressNotRequired(): boolean {
    return this.configData?.appPages?.location?.isAddressNotRequired || false;
  }

  get showPostCodeAsInput(): boolean {
    return this.configData?.appPages?.location.showPostCodeAsInput;
  }

  get hideJurisdictionCheckButton(): boolean {
    return this.configData?.appPages?.location.hideJurisdictionCheckButton;
  }

  get useDropdownForArea(): boolean {
    return this.configData?.appPages?.location.useDropdownForArea;
  }

  get getAreaOptions(): Promise<AreaOption[]> {
    return fetch('../../assets/config/area-options.json').then(res => res.json());
  }

  get hideCommodityWater(): boolean {
    return this.configData?.appPages?.location?.hideCommodityWater || false;
  }

  get showWaterNote(): boolean {
    return this.configData?.appPages?.location.showWaterNote || false;
  }

  get showFeedInSystemJurisdictionCheckLabel(): boolean {
    return this.configData?.appPages?.location.showFeedInSystemJurisdictionCheckLabel || false;
  }

  get showCopyApplicationSectionForRegep(): boolean {
    return this.configData?.appPages?.location.showCopyApplicationSectionForRegep || false;
  }

  get showCopyApplicationSectionForNewInstallation(): boolean {
    return this.configData?.appPages?.location.showCopyApplicationSectionForNewInstallation || false;
  }

  get showCopyApplicationSectionForChangeConnection(): boolean {
    return this.configData?.appPages?.location.showCopyApplicationSectionForChangeConnection || false;
  }

  get activateFieldReset(): boolean {
    return this.configData?.appPages?.location.activateFieldReset;
  }

  get technicianRegistrationHideWaterCommodity(): boolean {
    return this.configData?.appPages?.newRegistration.hideWaterCommodity;
  }

  get technicianRegistrationHideCustomerRoleOption(): boolean {
    return this.configData?.appPages?.newRegistration.hideCustomerRoleOption;
  }

  get showDataProtectionRegistrationLink(): boolean {
    return this.configData?.appPages?.newRegistration.showDataProtectionRegistrationLink;
  }

  get getDataProtectionRegistrationUrlLink(): string {
    return this.configData?.appPages?.newRegistration.dataProtectionRegistrationUrlLink;
  }

  get responsibleInstaller(): boolean {
    return this.configData?.appPages?.personalData.responsibleInstaller;
  }

  get showResponsibleInstallerBdewId(): boolean {
    return this.configData?.appPages?.personalData.showResponsibleInstallerBdewId || false;
  }

  get showBuildingType(): boolean {
    return this.configData?.appPages?.personalData.showBuildingType;
  }

  get showConnectionUser(): boolean {
    return this.configData?.appPages?.personalData.showConnectionUser;
  }

  get showDistrict(): boolean {
    return this.configData?.appPages?.personalData.showDistrict;
  }

  showLandownerPerApplication(appName: string): boolean {
    return this.configData?.appPages?.personalData.showLandownerPerApplication[appName] ?? false;
  }

  showInvoiceAddressPerApplication(appName: string): boolean {
    return this.configData?.appPages?.personalData.showInvoiceAddressPerApplication[appName] ?? false;
  }

  get buildingTypeRequired(): boolean {
    return this.configData?.appPages?.personalData.buildingTypeRequired;
  }

  get showNewMainsConnectionBuildingType(): boolean {
    return this.configData?.appPages?.personalData.showNewMainsConnectionBuildingType;
  }

  get showFeedInSystemBuildingType(): boolean {
    return this.configData?.appPages?.personalData.showFeedInSystemBuildingType;
  }

  get presetEmail(): boolean {
    return this.configData?.appPages?.personalData.presetEmail;
  }

  get telephoneEmailRequired(): boolean {
    return this.configData?.appPages?.personalData.telephoneEmailRequired;
  }

  get showLandownerHintText(): boolean {
    return this.configData?.appPages?.personalData.showLandownerHintText;
  }

  get shouldRestrictStreetNumberTo5Chars(): boolean {
    return this.configData?.appPages?.personalData?.shouldRestrictStreetNumberTo5Chars || false;
  }

  get shouldMarkFirstAndLastNameAsMandatory(): boolean {
    return this.configData?.appPages?.personalData.shouldMarkFirstAndLastNameAsMandatory || false;
  }

  get hideDefaultBuildingType(): boolean {
    return this.configData?.appPages?.personalData.hideDefaultBuildingType || false;
  }

  get showReviewSectionMessaging(): boolean {
    return this.configData?.appPages?.personalData?.showReviewSectionMessaging ?? true;
  }

  get siteWaterCheck(): boolean {
    return this.configData?.appPages?.technicianData.siteWaterCheck;
  }

  get handoverLocationPowerRequired(): boolean {
    return this.configData?.appPages?.technicianData.handoverLocationPowerRequired;
  }

  get handoverLocationPowerDefaultValue(): string {
    return this.configData?.appPages?.technicianData.handoverLocationPowerDefaultValue;
  }

  get handoverLocationPowerRequiredForChangeHouseConnection(): boolean {
    return this.configData?.appPages?.technicianData.handoverLocationPowerRequiredForChangeHouseConnection || false;
  }

  get handoverLocationPowerDefaultValueForMainsConnection(): string {
    return this.configData?.appPages?.technicianData.handoverLocationPowerDefaultValueForMainsConnection ?? '';
  }

  get showUsageSection(): boolean {
    return this.configData?.appPages?.technicianData?.showUsageSection || false;
  }

  get heatingLoadRequired(): boolean {
    return this.configData?.appPages?.technicianData.heatingLoadRequired;
  }

  get addPowerDevice(): boolean {
    return this.configData?.appPages?.technicianData.addPowerDevice;
  }

  get peakFlowRequired(): boolean {
    return this.configData?.appPages?.technicianData.peakFlowRequired;
  }

  get showPeakFlowConfirmationCheckboxes(): boolean {
    return this.configData?.appPages?.technicianData.showPeakFlowConfirmationCheckboxes;
  }

  get showMeterSwitch(): boolean {
    return this.configData?.appPages?.technicianData.showMeterSwitch;
  }

  get showTrenchLengthSection(): boolean {
    return this.configData?.appPages?.technicianData.showTrenchLengthSection;
  }

  get totalOutputRequired(): boolean {
    return this.configData?.appPages?.technicianData.totalOutputRequired;
  }

  get trenchLengthOver30(): boolean {
    return this.configData?.appPages?.technicianData.trenchLengthOver30;
  }

  get trenchLengthFixed(): boolean {
    return this.configData?.appPages?.technicianData.trenchLengthFixed;
  }

  get showCableLengthFixed(): boolean {
    return this.configData?.appPages?.technicianData.showCableLengthFixed;
  }

  get otherInquiriesSection(): boolean {
    return this.configData?.appPages?.technicianData.otherInquiriesSection;
  }

  get showReasonForReinforcement(): boolean {
    return this.configData?.appPages?.technicianData.showReasonForReinforcement;
  }

  get sampleDocuments(): boolean {
    return this.configData?.appPages?.documentSubmission.sampleDocuments;
  }

  get showSampleDocumentsForFeedIn(): boolean {
    return this.configData?.appPages?.documentSubmission?.showSampleDocumentsForFeedIn || false;
  }

  get floorPlanDocumentRequiredForTemporaryHouse(): boolean {
    return this.configData?.appPages?.documentSubmission.floorPlanDocumentRequiredForTemporaryHouse;
  }

  get floorPlanDocumentRequiredForChangeHouseConnection(): boolean {
    return this.configData?.appPages?.documentSubmission.floorPlanDocumentRequiredForChangeHouseConnection;
  }

  get showOrdnanceFileUpload(): boolean {
    return this.configData?.appPages?.documentSubmission.showOrdnanceFileUpload;
  }

  get sitePlanDocumentRequiredForChangeHouseConnection(): boolean {
    return this.configData?.appPages?.documentSubmission.sitePlanDocumentRequiredForChangeHouseConnection;
  }

  get sitePlanDocumentRequiredForNewMainsConnection(): boolean {
    return this.configData?.appPages?.documentSubmission?.sitePlanDocumentRequiredForNewMainsConnection || false;
  }

  get sitePlanDocumentRequiredForNewMainsConnectionElectricity(): boolean {
    return this.configData?.appPages?.documentSubmission?.sitePlanDocumentRequiredForNewMainsConnectionElectricity || false;
  }

  get sitePlanDocumentRequiredForTemporaryHouse(): boolean {
    return this.configData?.appPages?.documentSubmission?.sitePlanDocumentRequiredForTemporaryHouse || false;
  }

  get showDocumentHeaderHintIcon(): boolean {
    return this.configData?.appPages?.documentSubmission.showDocumentHeaderHintIcon;
  }

  get showNewDevAreaSection(): boolean {
    return this.configData?.appPages?.documentSubmission.showNewDevAreaSection;
  }

  get showDocumentSubmisiionEarthworkArea(): boolean {
    return this.configData?.appPages?.documentSubmission.showEarthworkArea;
  }

  get desiredDateDisabledDays(): [number, number] {
    return this.configData?.appPages?.documentSubmission.desiredDateDisabledDays || null;
  }

  get showCommentField(): boolean {
    return this.configData?.appPages?.documentSubmission.showCommentField;
  }

  get showTechnicalConditionsMetCheckbox(): boolean {
    return this.configData?.appPages?.documentSubmission.showTechnicalConditionsMetCheckbox;
  }

  get showDistrictHeatingRegulationsCheckbox(): boolean {
    return this.configData?.appPages?.documentSubmission.showDistrictHeatingRegulationsCheckbox;
  }

  get showPowerTermsChecked(): boolean {
    return this.configData?.appPages?.documentSubmission.showPowerTermsChecked;
  }

  get showDesiredDateOperationInfo(): boolean {
    return this.configData?.appPages?.documentSubmission.showDesiredDateOperationInfo;
  }

  get showSitePlanDocument(): boolean {
    return this.configData?.appPages?.documentSubmission.showSitePlanDocument;
  }

  get showSitePlanDocumentForConversionToGas(): boolean {
    return this.configData?.appPages?.documentSubmission.showSitePlanDocumentForConversionToGas ?? false;
  }

  get planDocumentsNotRequiredForRoles(): string[] {
    return this.configData?.appPages?.documentSubmission.planDocumentsNotRequiredForRoles || [];
  }

  get showAddMoreProductsButton(): any {
    return this.configData?.appPages?.documentSubmission.showAddMoreProductsButton || {};
  }

  get showApprovalEmptyTubeDocument(): boolean {
    return this.configData?.appPages?.documentSubmission.showApprovalEmptyTubeDocument;
  }

  get showIfNecessaryInfoText(): boolean {
    return this.configData?.appPages?.documentSubmission.showIfNecessaryInfoText;
  }

  get showProofOfPropertyOwnerDocument(): boolean {
    return this.configData?.appPages?.documentSubmission.showProofOfPropertyOwnerDocument;
  }

  get showAdditionalComment(): boolean {
    return this.configData?.appPages?.documentSubmission.showAdditionalComment;
  }

  get showInquiryDesiredDate(): boolean {
    return this.configData?.appPages?.documentSubmission.showInquiryDesiredDate;
  }

  get showMainsConnectionApproval(): boolean {
    return this.configData?.appPages?.documentSubmission.showMainsConnectionApproval;
  }

  get showExcerptOrOverviewPlanDocument(): boolean {
    return this.configData?.appPages?.documentSubmission.showExcerptOrOverviewPlanDocument;
  }

  get showCorrectnessOfInformationCheckbox(): boolean {
    return this.configData?.appPages?.documentSubmission.showCorrectnessOfInformationCheckbox;
  }

  get showWorksBeginsAfterOfferLable(): boolean {
    return this.configData?.appPages?.documentSubmission.showWorksBeginsAfterOfferLable;
  }

  get showGenericBaseDataSection(): boolean {
    return this.configData?.appPages?.technicianData.showGenericBaseDataSection;
  }

  get showSwsPlotSize(): boolean {
    return this.configData?.appPages?.technicianData.showSwsPlotSize;
  }

  get showSwsPlotSizeForMainsConnection(): boolean {
    return this.configData?.appPages?.technicianData.showSwsPlotSizeForMainsConnection;
  }

  get isDecommissioningOfGridConnection(): boolean {
    return this.configData?.appPages?.technicianData.isDecommissioningOfGridConnection;
  }
  get showHALengthsSection(): boolean {
    return this.configData?.appPages?.technicianData.showHALengthsSection;
  }

  get showTempConnectionsInfo(): boolean {
    return this.configData?.appPages?.technicianData.showTempConnectionsInfo ?? false;
  }

  get showDeterminePriceButton(): boolean {
    return this.configData?.appPages?.technicianData?.showDeterminePriceButton || false;
  }

  get showMeasuringPointCheckbox(): boolean {
    return this.configData?.appPages?.facilitiesData.showMeasuringPointCheckbox;
  }

  get showMeasuringPointNote(): boolean {
    return this.configData?.appPages?.facilitiesData.showMeasuringPointNote || false;
  }

  get showLabelAndUnit(): boolean {
    return this.configData?.appPages?.technicianData.showLabelAndUnit;
  }

  get reverseBuildingTypes(): boolean {
    return this.configData?.appPages?.location.reverseBuildingTypes;
  }

  get jurisdictionCheckEnabled(): boolean {
    return this.configData?.appPages?.jurisdiction?.checkEnabled;
  }

  get showTechnicalFormFirst(): boolean {
    return this.configData?.appPages?.jurisdiction.showTechnicalFormFirst;
  }

  get showGasMedia(): boolean {
    return this.configData?.appPages?.jurisdiction.showGasMedia;
  }

  get showDistrictHeatMedia(): boolean {
    return this.configData?.appPages?.jurisdiction.showDistrictHeatMedia;
  }

  get showPowerMediaForCustomer(): boolean {
    return this.configData?.appPages?.jurisdiction.showPowerMediaForCustomer;
  }

  get usePowerConnectionTypeForNewMains(): boolean {
    return this.configData?.appPages?.jurisdiction?.usePowerConnectionTypeForNewMains ?? false;
  }

  get showPowerConnectionInfo1(): boolean {
    return this.configData?.appPages?.jurisdiction?.showPowerConnectionInfo1 ?? false;
  }

  get showPowerConnectionInfo2(): boolean {
    return this.configData?.appPages?.jurisdiction?.showPowerConnectionInfo2 ?? false;
  }

  get jurisdictionShowApartmentOverSix(): boolean {
    return this.configData?.appPages?.jurisdiction.buildingType?.showApartmentOverSix;
  }

  get mapData(): MapData {
    return this.configData?.mapData;
  }

  get showConnectedLoadLabel(): boolean {
    return this.configData?.appPages?.technicianData.showConnectedLoadLabel;
  }

  get defaultStreetNumber(): string {
    return this.configData?.appPages?.personalData.defaultStreetNumber;
  }

  get defaultTotalPowerOutput(): string {
    return this.configData?.appPages?.technicianData.defaultTotalPowerOutput;
  }

  get defaultHeatingLoadValue(): string {
    return this.configData?.appPages?.technicianData.defaultHeatingLoadValue;
  }

  get heatingLoadDefaultValueForMainsConnection(): string {
    return this.configData?.appPages?.technicianData.heatingLoadDefaultValueForMainsConnection ?? '';
  }

  get defaultPeakFlowValue(): string {
    return this.configData?.appPages?.technicianData.defaultPeakFlowValue;
  }

  get hidePeakFlowInput(): boolean {
    return this.configData?.appPages?.technicianData.hidePeakFlowInput;
  }

  get showTotalFeedInPowerOfTheEnergyProducer(): boolean {
    return !!this.configData?.appPages?.technicianData.showTotalFeedInPowerOfTheEnergyProducer;
  }

  get skipTotalFeedInPowerValidation(): boolean {
    return !!this.configData?.appPages?.technicianData.skipTotalFeedInPowerValidation;
  }

  get handoverLocationPowerDefaultValueForConversionOfGas(): number | null {
    return this.configData?.appPages?.technicianData.handoverLocationPowerDefaultValueForConversionOfGas;
  }

  get showPublicFacilityForCustomer(): boolean {
    return this.configData?.appPages?.technicianData.showPublicFacilityForCustomer;
  }

  get showLabelForTransformerSize(): boolean {
    return this.configData?.appPages?.technicianData.showLabelForTransformerSize;
  }

  get showResidentialUnitsForCustomer(): boolean {
    return this.configData?.appPages?.technicianData.showResidentialUnitsForCustomer;
  }

  get showBusinessForCustomer(): boolean {
    return this.configData?.appPages?.technicianData.showBusinessForCustomer;
  }

  get showSiteFacilitiesCheckboxExclusively(): boolean {
    return this.configData?.appPages?.technicianData.showSiteFacilitiesCheckboxExclusively;
  }

  get hideSiteWaterCheckbox(): boolean {
    return !!this.configData?.appPages?.technicianData.hideSiteWaterCheckbox;
  }

  get showSiteElectricityCheckbox(): boolean {
    return this.configData?.appPages?.technicianData.showSiteElectricityCheckbox;
  }

  get heatingHiddenFields(): TechnicianDataHeatingHiddenFields {
    return this.configData?.appPages?.technicianData.heatingHiddenFields;
  }

  get waterHiddenFields(): TechnicianDataWaterHiddenFields {
    return this.configData?.appPages?.technicianData.waterHiddenFields;
  }

  get showEarthworkContributionCheckbox(): boolean {
    return this.configData?.appPages?.technicianData.showEarthworkContributionCheckbox;
  }

  get showGeneratingPlantCheckbox(): boolean {
    return this.configData?.appPages?.technicianData.showGeneratingPlantCheckbox || false;
  }

  get showFeedInSystemCheckbox(): boolean {
    return this.configData?.appPages?.technicianData.showFeedInSystemCheckbox;
  }

  get showWallboxCheckbox(): boolean {
    return this.configData?.appPages?.technicianData.showWallboxCheckbox;
  }

  get showInterestInFibreCheckbox(): boolean {
    return this.configData?.appPages?.technicianData.showInterestInFibreCheckbox;
  }

  get showPlannedOutputCheckbox(): boolean {
    return this.configData?.appPages?.technicianData?.showPlannedOutputCheckbox || false;
  }

  get showTelecomConnectionCheckbox(): boolean {
    return this.configData?.appPages?.technicianData.showTelecomConnectionCheckbox;
  }

  get showWidebandConnectionCheckbox(): boolean {
    return this.configData?.appPages?.technicianData.showWidebandConnectionCheckbox;
  }

  get showPeakFlowRateAsVolumeFlowRate(): boolean {
    return this.configData?.appPages?.technicianData.showPeakFlowRateAsVolumeFlowRate;
  }

  get showTotalOutputField(): boolean {
    return this.configData?.appPages?.technicianData.showTotalOutputField;
  }

  get showStandardPowerWarning(): boolean {
    return this.configData?.appPages?.technicianData.showStandardPowerWarning;
  }

  get showFuseStrengthDropdown(): boolean {
    return this.configData?.appPages?.technicianData.showFuseStrengthDropdown;
  }

  get showOnlyPeakFlow(): boolean {
    return this.configData?.appPages?.technicianData.showOnlyPeakFlow;
  }

  get showGasAppliance(): boolean {
    return this.configData?.appPages?.technicianData.showGasAppliance;
  }

  get showDeviceRemovedCheckbox(): boolean {
    return this.configData?.appPages?.technicianData.showDeviceRemovedCheckbox;
  }

  get hideBaseDataForGridConnectionClosure(): boolean {
    return this.configData?.appPages?.technicianData.hideBaseDataForGridConnectionClosure;
  }

  get hideBaseDataForNewMainsPower(): boolean {
    return this.configData?.appPages?.technicianData.hideBaseDataForNewMainsPower ?? false;
  }

  get hidePowerDeviceAddForNewMains(): boolean {
    return this.configData?.appPages?.technicianData.hidePowerDeviceAddForNewMains ?? false;
  }

  get hideOrderProcessSubmittedMessage(): boolean {
    return this.configData?.appPages?.technicianData.hideOrderProcessSubmittedMessage ?? false;
  }

  get usePowerConnectionExtendedOptionsForNewMains(): boolean {
    return this.configData?.appPages?.technicianData.usePowerConnectionExtendedOptionsForNewMains ?? false;
  }

  get showTempConnectionRegistrationForNewMains(): boolean {
    return this.configData?.appPages?.technicianData.showTempConnectionRegistrationForNewMains ?? false;
  }

  get useOrderHistoryPagination(): boolean {
    return this.configData?.appPages?.technicianData.useOrderHistoryPagination ?? false;
  }

  get showInfoTextRequiredFusePowerLarger200A(): boolean {
    return this.configData?.appPages?.technicianData.showInfoTextRequiredFusePowerLarger200A ?? false;
  }

  get showSpecialConsumers(): boolean {
    return this.configData?.appPages?.technicianData.showSpecialConsumers ?? true;
  }

  get hideFeedInOperationModeSubRadioButtons(): boolean {
    return this.configData?.appPages?.technicianData?.hideFeedInOperationModeSubRadioButtons || false;
  }

  get showFeedInOperationModeIntendedFRE(): boolean {
    return this.configData?.appPages?.technicianData?.showFeedInOperationModeIntendedFRE || false;
  }

  get hideAlternativeConceptCheckBox(): boolean {
    return this.configData?.appPages?.technicianData?.hideAlternativeConceptCheckBox || false;
  }

  get showStorageSubsection(): boolean {
    return this.configData?.appPages?.technicianData?.showStorageSubsection || false;
  }

  get showTechnicalDataFeverPlantSection(): boolean {
    return this.configData?.appPages?.technicianData?.showTechnicalDataFeverPlantSection;
  }

  get isMeterShutdown(): boolean {
    return this.configData?.appPages?.technicianData.isMeterShutdown;
  }

  get showMeterInstallation(): boolean {
    return this.configData?.appPages?.technicianData?.showMeterInstallation;
  }

  get showRequestedTotalPower(): boolean {
    return this.configData?.appPages?.technicianData.showRequestedTotalPower;
  }

  get minDocumentDescriptionDate(): number {
    return this.configData?.appPages?.documentSubmission.minDescriptionDate;
  }

  get minDescriptionDateMeterMounting(): number {
    return this.configData?.appPages?.documentSubmission.minDescriptionDateMeterMounting;
  }

  get minBuildingConDate(): number {
    return this.configData?.appPages?.documentSubmission.minBuildingConnectionDate;
  }

  get showWaterRegistryDocument(): boolean {
    return this.configData?.appPages?.documentSubmission.showWaterRegistryDocument;
  }

  get desiredDate(): boolean {
    return this.configData?.appPages?.documentSubmission.desiredDate;
  }

  get showGas3(): boolean {
    return this.configData?.appPages?.documentSubmission.showGas3;
  }

  get showChimneySweepers(): boolean {
    return this.configData?.appPages?.documentSubmission.showChimneySweepers;
  }

  get showCurrentAccountDocument(): boolean {
    return this.configData?.appPages?.documentSubmission.showCurrentAccountDocument;
  }

  get hideGasConfirmationCheckboxes(): boolean {
    return this.configData?.appPages?.documentSubmission.hideGasConfirmationCheckboxes || false;
  }

  get showHelpText(): boolean {
    return this.configData?.appPages?.documentSubmission.showHelpText;
  }

  get showFiltersForCustomer(): boolean {
    return this.configData?.appPages?.jurisdictionOverview.showFiltersForCustomer;
  }

  get hideTechnicianColumn(): boolean {
    return this.configData?.appPages?.jurisdictionOverview.hideTechnicianColumn || false;
  }

  get showCompanyNameColumn(): boolean {
    return this.configData?.appPages?.jurisdictionOverview.showCompanyNameColumn || false;
  }

  get showTechnicianColumn(): boolean {
    return this.configData?.appPages?.jurisdictionOverview.showTechnicianColumn || false;
  }

  get annualConsumptionNotRequiredWhenMeterTypeIsProfile(): boolean {
    return this.configData?.appPages?.facilitiesData?.annualConsumptionNotRequiredWhenMeterTypeIsProfile || false;
  }

  get showChangeHouseConnectionForCustomer(): boolean {
    return this.configData?.appPages?.panelData.showChangeHouseConnectionForCustomer;
  }

  get showFeedInSystemForCustomer(): boolean {
    return this.configData?.appPages?.panelData?.showFeedInSystemForCustomer || false;
  }

  get showRegepForCustomer(): boolean {
    return this.configData?.appPages?.panelData?.showRegepForCustomer || false;
  }

  get showBlockIntervalsField(): boolean {
    return this.configData?.appPages?.facilitiesData.showBlockIntervalsField;
  }

  get showControlUnitAvailableCheckbox(): boolean {
    return this.configData?.appPages?.facilitiesData.showControlUnitAvailableCheckbox;
  }

  get showNewMeterSection(): boolean {
    return this.configData?.appPages?.facilitiesData.showNewMeterSection;
  }

  get hideNewMeterSectionForSystemMerge(): boolean {
    return this.configData?.appPages?.facilitiesData.hideNewMeterSectionForSystemMerge || false;
  }

  get hideNewMeterSectionForUsageAdjustment(): boolean {
    return this.configData?.appPages?.facilitiesData.hideNewMeterSectionForUsageAdjustment || false;
  }

  get showOldMeterSection(): boolean {
    return this.configData?.appPages?.facilitiesData.showOldMeterSection;
  }

  get showOldMeterField(): boolean {
    return this.configData?.appPages?.facilitiesData.showOldMeterField;
  }

  get oldMeterNumberRegex(): boolean {
    return this.configData?.appPages?.facilitiesData.oldMeterNumberRegex;
  }

  get numberOfApartmentRegex(): boolean {
    return this.configData?.appPages?.facilitiesData.numberOfApartmentRegex;
  }

  get showSelectMountAsDropdown(): boolean {
    return this.configData?.appPages?.facilitiesData.showSelectMountAsDropdown;
  }

  get showFloor(): boolean {
    return this.configData?.appPages?.facilitiesData.showFloor;
  }

  get showCommunicationAndMountType(): boolean {
    return this.configData?.appPages?.facilitiesData.showCommunicationAndMountType;
  }

  get showTypeOfUseAsMeterType(): boolean {
    return this.configData?.appPages?.facilitiesData.showTypeOfUseAsMeterType;
  }

  get facilityHeatingHiddenFields(): FacilitiesHeatingHiddenFields {
    return this.configData?.appPages?.facilitiesData.heatingHiddenFields;
  }

  get facilityHiddenDropdownData(): FacilitiesHiddenDropdownData {
    return this.configData?.appPages?.facilitiesData.hiddenDropdownData;
  }

  get showDropdownsForNewInstallation(): boolean {
    return this.configData?.appPages?.facilitiesData.showDropdownsForNewInstallation;
  }

  get isPlaceOfMeterOptional(): boolean {
    return this.configData?.appPages?.facilitiesData.isPlaceOfMeterOptional || false;
  }

  get isPeakFlowRequired(): boolean {
    return this.configData?.appPages?.facilitiesData.isPeakFlowRequired || false;
  }

  get peakFlowNotRequiredForUsageAdjustment(): boolean {
    return this.configData?.appPages?.facilitiesData.peakFlowNotRequiredForUsageAdjustment || false;
  }

  get showOnly3PointMounting(): boolean {
    return this.configData?.appPages?.facilitiesData?.showOnly3PointMounting || false;
  }

  get showAmount(): boolean {
    return this.configData?.appPages?.facilitiesData.showAmount;
  }

  get showTextTempIcon(): boolean {
    return this.configData?.appPages?.facilitiesData.showTextTempIcon;
  }

  get facilityPowerHiddenFields(): FacilitiesPowerHiddenFields {
    return this.configData?.appPages?.facilitiesData.powerHiddenFields;
  }

  get facilityWaterHiddenFields(): FacilitiesWaterHiddenFields {
    return this.configData?.appPages?.facilitiesData.waterHiddenFields;
  }

  get showSkMeterMountHeat(): boolean {
    return this.configData?.appPages?.facilitiesData.showSkMeterMountHeat;
  }

  get licenseAgreementFile(): boolean {
    return this.configData?.appPages?.technicalData.licenseAgreementFile;
  }

  get showManufacturerAndType(): boolean {
    return this.configData?.appPages?.technicalData?.showManufacturerAndType || false;
  }

  get showFotoProofPhotovoltaicModules(): boolean {
    return this.configData?.appPages?.technicalData?.showFotoProofPhotovoltaicModules || false;
  }

  get showEnergyProducerIdInfoText(): boolean {
    return this.configData?.appPages?.technicalData?.showEnergyProducerIdInfoText || false;
  }

  get hideOtherFuelsSystemType(): boolean {
    return this.configData?.appPages?.technicalData?.hideOtherFuelsSystemType || false;
  }

  get generatorPowerInWatts(): boolean {
    return this.configData?.appPages?.technicalData?.generatorPowerInWatts || false;
  }

  get shouldSelectionOfMeasuringConceptBeNoneMandatory(): boolean {
    return this.configData?.appPages?.technicalData?.shouldSelectionOfMeasuringConceptBeNoneMandatory || false;
  }

  get showNumberOfCorrespondingFigureAccordingToFnn(): boolean {
    return this.configData?.appPages?.technicalData?.showNumberOfCorrespondingFigureAccordingToFnn || false;
  }

  get show9EegDetails(): boolean {
    return this.configData?.appPages?.technicalData?.show9EegDetails ?? true;
  }

  get hideControlAndOnDemandSupplyOfFeedInEnergy(): boolean {
    return this.configData?.appPages?.technicalData?.hideControlAndOnDemandSupplyOfFeedInEnergy || false;
  }

  get setCommissioningDateNoneMandatory(): boolean {
    return this.configData?.appPages?.technicalData?.setCommissioningDateNoneMandatory || false;
  }

  get floatNumberFeedinSystemMaxPower(): number {
    return this.configData?.appPages?.technicalData?.floatNumberFeedinSystemMaxPower || 2;
  }

  get showMaxPowerLabelAccordingToSubproduct(): boolean {
    return this.configData?.appPages?.technicalData?.showMaxPowerLabelAccordingToSubproduct || false;
  }

  get modernMeterSubtypeList(): boolean {
    return this.configData?.appPages?.facilitiesData.modernMeterSubtypeList;
  }

  get counterSizeList(): boolean {
    return this.configData?.appPages?.facilitiesData.counterSizeList;
  }

  get hideCounterSize(): boolean {
    return this.configData?.appPages?.facilitiesData?.hideCounterSize || false;
  }

  get hideMeterCompliance(): boolean {
    return this.configData?.appPages?.facilitiesData?.hideMeterCompliance || false;
  }

  get hideMeterMountPluginMounting(): boolean {
    return this.configData?.appPages?.facilitiesData?.hideMeterMountPluginMounting || false;
  }

  get isSpecialApplianceSectionRequired(): boolean {
    return this.configData?.appPages?.facilitiesData.isSpecialApplianceSectionRequired;
  }

  get annualConsumptionNotRequired(): boolean {
    return this.configData?.appPages?.facilitiesData.annualConsumptionNotRequired || false;
  }

  get selectMountNotRequired(): boolean {
    return this.configData?.appPages?.facilitiesData.selectMountNotRequired || false;
  }

  get myData(): Data {
    return this.configData?.appPages?.data;
  }

  get showE7AuditReportInverter(): boolean {
    return this.configData?.appPages?.regep?.showE7AuditReportInverter ?? true;
  }

  get showMeterReadingField(): boolean {
    return this.configData?.appPages?.facilitiesData.showMeterReadingField;
  }

  get showMeterImageUpload(): boolean {
    return this.configData?.appPages?.facilitiesData.showMeterImageUpload;
  }

  get showChimneySweepConfirmationSection(): boolean {
    return this.configData?.appPages?.facilitiesData.showChimneySweepConfirmationSection;
  }

  get showGasDeviceSection(): boolean {
    return this.configData?.appPages?.facilitiesData.showGasDeviceSection;
  }

  get showOtherLocationLabel(): boolean {
    return this.configData?.appPages?.facilitiesData.showOtherLocationLabel;
  }

  get showDefaultDeviceSubSection(): boolean {
    return this.configData?.appPages?.facilitiesData.showDefaultDeviceSubSection;
  }

  get showSpecialApplienceSection(): boolean {
    return this.configData?.appPages?.facilitiesData.showSpecialApplienceSection ?? true;
  }

  get showGasTechnicalDataForComercial(): boolean {
    return this.configData?.appPages?.facilitiesData?.showGasTechnicalDataForComercial || false;
  }

  get oldApplicationValidationDate(): string {
    return this.configData?.appPages?.facilitiesData.oldApplicationValidationDate;
  }

  get isMeterDataDependentOnOperator(): boolean {
    return this.configData?.appPages?.facilitiesData.isMeterDataDependentOnOperator;
  }

  get hideMeterDataAlignmentGroupForPower(): boolean {
    return this.configData?.appPages?.facilitiesData.hideMeterDataAlignmentGroupForPower || false;
  }

  get hideMeterDataAlignmentGroupForGas(): boolean {
    return this.configData?.appPages?.facilitiesData.hideMeterDataAlignmentGroupForGas || false;
  }

  get hideMeterDataAlignmentGroupForWater(): boolean {
    return this.configData?.appPages?.facilitiesData.hideMeterDataAlignmentGroupForWater || false;
  }

  get hideMeterDataAlignmentGroupForHeating(): boolean {
    return this.configData?.appPages?.facilitiesData.hideMeterDataAlignmentGroupForHeating || false;
  }

  get isCounterSizeSelectedBasedOnPeakFlow(): boolean {
    return this.configData?.appPages?.facilitiesData.isCounterSizeSelectedBasedOnPeakFlow || false;
  }

  get hideNewFacilityData(): boolean {
    return this.configData?.appPages?.facilitiesData.hideNewFacilityData || false;
  }

  get showRemoteReadoutUnderCounterSize(): boolean {
    return this.configData?.appPages?.facilitiesData.showRemoteReadoutUnderCounterSize || false;
  }

  get hideRemoteReadoutOption(): boolean {
    return this.configData?.appPages?.facilitiesData.hideRemoteReadoutOption || false;
  }

  get showTotalHeatOutput(): boolean {
    return this.configData?.appPages?.facilitiesData.showTotalHeatOutput;
  }

  get showMeterLocation(): boolean {
    return this.configData?.appPages?.facilitiesData.showMeterLocation;
  }

  get showMeterLocationForEveryComodity(): boolean {
    return this.configData?.appPages?.facilitiesData.showMeterLocationForEveryComodity;
  }

  get hasSimultaneouslyRequiredTotalPowerAfterInstallationChange(): boolean {
    return this.configData?.appPages?.facilitiesData.hasSimultaneouslyRequiredTotalPowerAfterInstallationChange || false;
  }

  get isMeterNumberRequiredOnFacilitiesData(): boolean {
    return this.configData?.appPages?.facilitiesData?.isMeterNumberRequired;
  }

  get hideMeterAction(): boolean {
    return this.configData?.appPages?.facilitiesData?.hideMeterAction || false;
  }

  get hideOldMeterAction(): boolean {
    return this.configData?.appPages?.facilitiesData?.hideOldMeterAction || false;
  }

  get pageTitle(): string {
    return this.configData?.labels?.app?.pageTitle;
  }

  get regexData(): KeyValueData {
    return this.configData?.regex;
  }

  get showLanguageSelector(): boolean {
    return this.configData?.showLanguageSelector;
  }

  get hideGenerationFacilityAdditionalData(): boolean {
    return this.configData?.appPages?.facilitiesData.hideGenerationFacilityAdditionalData;
  }

  get showWaterSystemComponents(): boolean {
    return this.configData?.appPages?.facilitiesData?.showWaterSystemComponents || false;
  }

  get copyExistingTechnicalDataConfiguration(): boolean {
    return this.configData?.appPages?.facilitiesData?.copyExistingTechnicalDataConfiguration || false;
  }

  get hideLocationField(): boolean {
    return this.configData?.appPages?.facilitiesData?.hideLocationField || false;
  }

  get powerConnectionShowEquipmentAvailableCheckboxes() {
    return this.configData?.appPages?.technicianData?.powerConnectionShowEquipmentAvailableCheckboxes || false;
  }

  get showOtherCommoditiesForInstallation() {
    return this.configData?.appPages?.technicianData?.showOtherCommoditiesForInstallation || false;
  }

  get showTechnicianDataEarthWorkArea() {
    return this.configData?.appPages?.technicianData?.showEarthWorkArea || false;
  }

  get minHeatDescriptionDate(): number {
    return this.configData?.appPages?.documentSubmission.minHeatDescriptionDate;
  }

  get minDistrictHeatingDescriptionDate(): number {
    return this.configData?.appPages?.documentSubmission.minDistrictHeatingDescriptionDate;
  }

  get showDisposalOfOilTankRadio(): boolean {
    return this.configData?.appPages?.technicianData.showDisposalOfOilTankRadio;
  }

  get showEmailInsteadOfMobile(): boolean {
    return this.configData?.appPages?.personalData.showEmailInsteadOfMobile;
  }

  get showEmail(): boolean {
    return this.configData?.appPages?.personalData.showEmail || false;
  }

  get showEmailRequired(): boolean {
    return this.configData?.appPages?.personalData.showEmailRequired || false;
  }

  get showRepeatEmail(): boolean {
    return this.configData?.appPages?.personalData.showRepeatEmail || false;
  }

  get setDefaultToggleStates(): boolean {
    return this.configData?.appPages?.personalData.setDefaultToggleStates || false;
  }

  get showRlmMeasurement(): boolean {
    return this.configData?.appPages?.facilitiesData?.showRlmMeasurement || false;
  }

  get showMeterLocationInfoPopup(): boolean {
    return this.configData?.appPages?.facilitiesData.showMeterLocationInfoPopup || false;
  }

  get lowercaseGasPressureMeasurementUnits(): boolean {
    return this.configData?.appPages?.technicianData.lowercaseGasPressureMeasurementUnits;
  }

  get showSubjectToApprovalCheckbox(): boolean {
    return this.configData?.appPages?.facilitiesData.showSubjectToApprovalCheckbox;
  }

  get hideZerezId(): boolean {
    return this.configData?.appPages?.facilitiesData.hideZerezId || false;
  }

  get showFloorPlanFileUpload(): boolean {
    return this.configData?.appPages?.documentSubmission.showFloorPlanFileUpload;
  }

  get hideFloorPlanFileUpload(): boolean {
    return this.configData?.appPages?.documentSubmission.hideFloorPlanFileUpload;
  }

  get showFloorPlanWithEntryText(): boolean {
    return this.configData?.appPages?.documentSubmission.showFloorPlanWithEntryText ?? false;
  }

  get hideMandatoryDocuments(): boolean {
    return this.configData?.appPages?.documentSubmission.hideMandatoryDocuments;
  }

  get showNoteIfStorageIsSelected(): boolean {
    return this.configData?.appPages?.documentSubmission?.showNoteIfStorageIsSelected || false;
  }

  get showNoteUploadAltMeasurementDoc(): boolean {
    return this.configData?.appPages?.documentSubmission?.showNoteUploadAltMeasurementDoc || false;
  }

  get showCustomerPowerOfAttorney(): boolean {
    return this.configData?.appPages?.documentSubmission?.showCustomerPowerOfAttorney || false;
  }

  get showNewDocuments(): boolean {
    return this.configData?.appPages?.documentSubmission?.showNewDocuments || false;
  }

  get showAcceptOfferCheckbox(): boolean {
    return this.configData?.appPages?.documentSubmission?.showAcceptOfferCheckbox || false;
  }

  get showProceedToRegepButton(): boolean {
    return this.configData?.appPages?.documentSubmission?.showProceedToRegepButton || false;
  }

  get showContactPersonOnDocumentsPage(): boolean {
    return this.configData?.appPages?.documentSubmission?.showContactPerson || false;
  }

  get hideManufacturerSheets(): boolean {
    return this.configData?.appPages?.documentSubmission?.hideManufacturerSheets || false;
  }

  get hideManufacturerGenerationSheet(): boolean {
    return this.configData?.appPages?.documentSubmission?.hideManufacturerGenerationSheet || false;
  }

  get hideSinglePoleOverviewPlan(): boolean {
    return this.configData?.appPages?.documentSubmission?.hideSinglePoleOverviewPlan || false;
  }

  get showSitePlanLabel(): boolean {
    return this.configData?.appPages?.documentSubmission?.showSitePlanLabel || false;
  }

  get showNoteFindRelevantDocsChecklist(): boolean {
    return this.configData?.appPages?.documentSubmission?.showNoteFindRelevantDocsChecklist || false;
  }

  get trenchLengthFieldsRequired(): boolean {
    return this.configData?.appPages?.technicianData.trenchLengthFieldsRequired;
  }

  get showAdditionalMeasuringConcepts(): boolean {
    return this.configData?.appPages?.technicianData.showAdditionalMeasuringConcepts;
  }

  get showDropdownMeassuringConceptInMeterData(): boolean {
    return this.configData?.appPages?.technicianData.showDropdownMeassuringConceptInMeterData || false;
  }

  get switchFeedInSystemExcessEnergyRadioButtons(): boolean {
    return this.configData?.appPages?.technicianData?.switchFeedInSystemExcessEnergyRadioButtons || false;
  }

  get hideSystemOperationSection(): boolean {
    return this.configData?.appPages?.technicianData.hideSystemOperationSection;
  }

  get hideInstallationSection(): boolean {
    return this.configData?.appPages?.technicianData.hideInstallationSection;
  }

  get showOrderProcessChangeBtns(): boolean {
    return this.configData?.appPages?.employeeOverview.showOrderProcessChangeBtns;
  }

  get showAcceptRejectApplicationBtns(): boolean {
    return this.configData?.appPages?.employeeOverview?.showAcceptRejectApplicationBtns || false;
  }

  get showSapOfferAndOrderId(): boolean {
    return this.configData?.appPages?.employeeOverview?.showSapOfferAndOrderId || false;
  }

  get showCompactStationNewLabel(): boolean {
    return this.configData?.appPages?.technicianData.heatingHiddenFields.showCompactStationNewLabel;
  }

  get showAdditionalMeterType(): boolean {
    return this.configData?.appPages?.facilitiesData.showAdditionalMeterType;
  }

  get showHeatingWeldingCertificateConfirmation(): boolean {
    return this.configData?.technicianRegistration?.showHeatingWeldingCertificateConfirmation || false;
  }

  get showSafetyTechnologyExemptionDocument(): boolean {
    return this.configData?.technicianRegistration?.showSafetyTechnologyExemptionDocument || false;
  }

  get showSafetyTechnologyAuthorizationDocument(): boolean {
    return this.configData?.technicianRegistration?.showSafetyTechnologyAuthorizationDocument || false;
  }

  get showProofOfVdeSubscriptionDocument(): boolean {
    return this.configData?.technicianRegistration?.showProofOfVdeSubscriptionDocument || false;
  }

  get enableActiveTechnicianEdit(): boolean {
    return this.configData?.technicianRegistration?.enableActiveTechnicianEdit || false;
  }

  get hideAddWaterTapOption(): boolean {
    return this.configData?.appPages?.facilitiesData.hideAddWaterTapOption || false;
  }

  get createAutomaticOffer(): boolean {
    return this.configData?.appPages?.data.createAutomaticOffer;
  }

  get requiredUnitCertificateDocument(): boolean {
    return this.configData?.appPages?.documentSubmission.requiredUnitCertificateDocument;
  }

  get requiredNaProtectionCertificateDocument(): boolean {
    return this.configData?.appPages?.documentSubmission.requiredNaProtectionCertificateDocument;
  }

  get requiredVdeDocument(): boolean {
    return this.configData?.appPages?.documentSubmission.requiredVdeDocument;
  }

  get requiredOverviewPlanSinglePoleDocument(): boolean {
    return this.configData?.appPages?.documentSubmission.requiredOverviewPlanSinglePoleDocument;
  }

  get hideExampleOfDocExcerptOrOverviewPlan(): boolean {
    return this.configData?.appPages?.documentSubmission?.hideExampleOfDocExcerptOrOverviewPlan || false;
  }

  get shoudCheckForUploadFileTypeAndSize(): boolean {
    return this.configData?.appPages?.documentSubmission?.shoudCheckForUploadFileTypeAndSize || false;
  }

  get floorPlanDocumentValidationStrategyForNewMains(): 'default' | 'custom_1' {
    return this.configData?.appPages?.documentSubmission?.floorPlanDocumentValidationStrategyForNewMains ?? 'default';
  }

  get showPlanWithEntryForConversionToGas(): boolean {
    return this.configData?.appPages?.documentSubmission?.showPlanWithEntryForConversionToGas || false;
  }

  get showConnectionOptions(): boolean {
    return this.configData?.appPages?.technicianData.showConnectionOptions;
  }

  get showFeedInPaymentsRecordedSeparately(): boolean {
    return this.configData?.appPages?.technicalData.showFeedInPaymentsRecordedSeparately ?? true;
  }

  get showPrimarySourcesRecordedSeparately(): boolean {
    return this.configData?.appPages?.technicalData.showPrimarySourcesRecordedSeparately ?? true;
  }

  getInfoBubblesData(pageName: string): {
    [key: string]: boolean;
  } {
    return this.configData?.appPages[pageName].infoBubbles;
  }

  get showApplicationIdInOffer(): boolean {
    return this.configData?.appPages?.employeeOverview?.showApplicationIdInOffer || false;
  }

  get showOrderOfferText(): boolean {
    return this.configData?.appPages?.employeeOverview?.showOrderOfferText || false;
  }

  get hideConversionOnHeatingRadioButton(): boolean {
    return this.configData?.appPages?.location?.hideConversionOnHeatingRadioButton || false;
  }

  get documentsForFeedInSystem() {
    const that = this;
    return {
      get power_of_attorney_1(): FieldSettings {
        return that.configData?.appPages?.documentSubmission?.feedInSystem?.power_of_attorney_1;
      },
      get e_1_application(): FieldSettings {
        return that.configData?.appPages?.documentSubmission?.feedInSystem?.e_1_application;
      },
      get e_2_data_sheet(): FieldSettings {
        return that.configData?.appPages?.documentSubmission?.feedInSystem?.e_2_data_sheet;
      },
      get e_3_memory_data(): FieldSettings {
        return that.configData?.appPages?.documentSubmission?.feedInSystem?.e_3_memory_data;
      },
      get e_4_uniform_certificate(): FieldSettings {
        return that.configData?.appPages?.documentSubmission?.feedInSystem?.e_4_uniform_certificate;
      },
      get e_6_certificate_for_network(): FieldSettings {
        return that.configData?.appPages?.documentSubmission?.feedInSystem?.e_6_certificate_for_network;
      },
      get e_7_requirements_for_test_report(): FieldSettings {
        return that.configData?.appPages?.documentSubmission?.feedInSystem?.e_7_requirements_for_test_report;
      },
      get b_11_overview_circuit_diagram(): FieldSettings {
        return that.configData?.appPages?.documentSubmission?.feedInSystem?.b_11_overview_circuit_diagram;
      },
      get registration_for_network_connection(): FieldSettings {
        return that.configData?.appPages?.documentSubmission?.feedInSystem?.registration_for_network_connection;
      },
      get site_plan_or_area_map(): FieldSettings {
        return that.configData?.appPages?.documentSubmission?.feedInSystem?.site_plan_or_area_map;
      },
      get proof_of_conformity_certificates_modules(): FieldSettings {
        return that.configData?.appPages?.documentSubmission?.feedInSystem?.proof_of_conformity_certificates_modules;
      },
      get proof_of_conformity_certificates_storage(): FieldSettings {
        return that.configData?.appPages?.documentSubmission?.feedInSystem?.proof_of_conformity_certificates_storage;
      },
      get measuring_concept(): FieldSettings {
        return that.configData?.appPages?.documentSubmission?.feedInSystem?.measuring_concept;
      },
      get e_5_test_report(): FieldSettings {
        return that.configData?.appPages?.documentSubmission?.feedInSystem?.e_5_test_report;
      },
      get e_8_commissioning_protocol(): FieldSettings {
        return that.configData?.appPages?.documentSubmission?.feedInSystem?.e_8_commissioning_protocol;
      },
      get ground_protocol(): FieldSettings {
        return that.configData?.appPages?.documentSubmission?.feedInSystem?.ground_protocol;
      },
      get site_plan(): FieldSettings {
        return that.configData?.appPages?.documentSubmission?.feedInSystem?.site_plan;
      },
      get unit_certificate(): FieldSettings {
        return that.configData?.appPages?.documentSubmission?.feedInSystem?.unit_certificate;
      },
      get na_protection_certificate(): FieldSettings {
        return that.configData?.appPages?.documentSubmission?.feedInSystem?.na_protection_certificate;
      },
      get vde_documents(): FieldSettings {
        return that.configData?.appPages?.documentSubmission?.feedInSystem?.vde_documents;
      },
      get data_sheet_modules(): FieldSettings {
        return that.configData?.appPages?.documentSubmission?.feedInSystem?.data_sheet_modules;
      },
      get data_sheet_generating_unit(): FieldSettings {
        return that.configData?.appPages?.documentSubmission?.feedInSystem?.data_sheet_generating_unit;
      },
      get overview_plan_single_pole(): FieldSettings {
        return that.configData?.appPages?.documentSubmission?.feedInSystem?.overview_plan_single_pole;
      },
      get pole_circuit_diagram_1(): FieldSettings {
        return that.configData?.appPages?.documentSubmission?.feedInSystem?.pole_circuit_diagram_1;
      },
    };
  }

  get documentsForConnectionInstallation() {
    const that = this;

    return {
      get hide_site_plan(): boolean {
        return !!that.configData?.appPages?.documentSubmission?.connectionInstallation?.hide_site_plan;
      },
    };
  }

  get eegProducerIdGenerator(): string {
    return this.configData?.eegProducerIdGenerator ?? 'default';
  }

  get enableRegulatoryDataSectionReview(): boolean {
    return !!this.configData.appPages.regulatoryData?.enableSectionReview;
  }

  get getNotificationOnSubmitRequest(): boolean {
    return this.configData?.notificationOnSubmitRequest ?? false;
  }

  get showMeterMountingAndChangeInstallationAdditionalData(): boolean {
    return this.configData?.appPages?.technicalData.showMeterMountingAndChangeInstallationAdditionalData ?? false;
  }

  get showConsumptionDeviceControl(): boolean {
    return this.configData?.appPages?.technicalData.showConsumptionDeviceControl ?? false;
  }

  get showFeedInInverterZerezId(): boolean {
    return this.configData?.appPages?.technicalData?.showFeedInInverterZerezId ?? false;
  }

  get useSeparatedPdfDownload(): boolean {
    return this.configData?.appPages?.documentSubmission?.useSeparatedPdfDownload || false;
  }

  get enableFeedInControllabilityOptions(): boolean {
    return this.configData?.appPages?.technicianData?.enableFeedInControllabilityOptions || false;
  }
}
